import {
  BillingSettings,
  ExtensionsSettings,
  FileSettings,
  WebhookConfigSet,
} from '../../constants/dataTypes';
import * as SettingTypes from './types';
import { AppThunkAction } from '../reduxTypes';
import UsersClient from '../../clients/UsersClient';
import { SetPaymentInfo } from '../payments/actions';
import { ChangeModel } from '../ui/types';
import { alertSnackbar, setPageReloading } from '../ui/actions';
import UiClient from '../../clients/UiClients';
import {
  WebhookForm,
  SettingsClient,
  UpdateWebhookConfigSetInput,
} from '../../clients/SettingsClient';
import { PortalConfigUtils } from '../../utils/PortalUtils';
import {
  CallApiWithNotification,
  ApiWithNotificationResponseType,
} from '../../clients/ApiService';
import {
  CrmSettings,
  CrmTableSettings,
  ERROR_CONSTS,
  MessageSettings,
  PortalSettings,
  ModuleSettingsItem,
} from '../../constants';
import {
  LOAD_PORTAL_SETTINGS,
  REMOVE_PORTAL_SETTINGS,
  LOAD_MESSAGE_SETTINGS,
  REMOVE_MESSAGE_SETTINGS,
} from './types';
import { BatchResult } from '../../utils/ApiUtils';

export const LoadSettingsStartAction = () => ({
  type: SettingTypes.LOAD_SETTINGS_START,
});

export const LoadSettingsErrorAction = (message: string) => ({
  type: SettingTypes.LOAD_SETTINGS_ERROR,
  error: message,
});

export const loadSettingsSuccess = (
  newSettings: SettingTypes.NewSettingsResponse,
) => ({
  type: SettingTypes.LOAD_SETTINGS_SUCCESS,
  data: newSettings,
});

export const loadPortalSettingsAction = (items: PortalSettings[]) => ({
  type: LOAD_PORTAL_SETTINGS,
  data: items,
});

export const removePortalSettingsAction = (items: PortalSettings[]) => ({
  type: REMOVE_PORTAL_SETTINGS,
  data: items,
});

export const setMessageSettingsAction = (items: MessageSettings[]) => ({
  type: LOAD_MESSAGE_SETTINGS,
  data: items,
});

export const removeMessageSettingsAction = (items: MessageSettings[]) => ({
  type: REMOVE_MESSAGE_SETTINGS,
  data: items,
});

export const LoadSettings = (userId?: string): AppThunkAction => async (
  dispatch,
  getState,
) => {
  let loadSettingsUserID = userId;
  if (!userId) {
    const { user } = getState();
    loadSettingsUserID = user.id;
  }
  dispatch(LoadSettingsStartAction());

  try {
    const response = await UsersClient.GetSettings(loadSettingsUserID);
    const { paymentInfo, ...newSettings } = response;
    dispatch(SetPaymentInfo(paymentInfo));
    dispatch(loadSettingsSuccess(newSettings));
  } catch (err) {
    const message = err.data
      ? err.data.message
      : 'Unexpected error loading settings';
    dispatch(LoadSettingsErrorAction(message));
  }
};

export const ClearSettings: () => SettingTypes.ClearSettingsAction = () => ({
  type: SettingTypes.CLEAR_SETTINGS,
});

export const clearSettingChanges = () => ({
  type: SettingTypes.CLEAR_SETTINGS_CHANGES,
});

export const updateSettingsStartAction = () => ({
  type: SettingTypes.UPDATE_SETTINGS_REQUEST,
});

export const updateSettingsFailedAction = (error: string) => ({
  type: SettingTypes.UPDATE_SETTINGS_FAILURE,
  error,
});

export const updateSettingsSucceededAction = () => ({
  type: SettingTypes.UPDATE_SETTINGS_SUCCESS,
});

/**
 * config: original Config
 * configChanges: changes in Portal Config
 * formChanges: TODO changes should be stored in backend
 */
export const uploadSettingsChanges = (
  config: any,
  configChanges: ChangeModel[],
): AppThunkAction => async dispatch => {
  function clear() {
    return { type: SettingTypes.CLEAR_SETTINGS_CHANGES };
  }

  dispatch(updateSettingsStartAction());

  const result = await CallApiWithNotification({
    executeFunction: UiClient.uploadPortalConfig,
    params: {
      config: PortalConfigUtils.MapConfigProperty(configChanges, config),
      source: 'module_management',
    },
    successMessage: `Main Settings are updated`,
    errorMessage: `These changes could not be saved.`,
    dispatch,
  });

  if (result.status) {
    dispatch(updateSettingsSucceededAction());
    dispatch(setPageReloading());
    dispatch(clear());
  } else {
    dispatch(updateSettingsFailedAction(result.data));
  }
};

export const saveMessageSettings = (
  messageSettings: MessageSettings,
): AppThunkAction => async dispatch => {
  dispatch(updateSettingsStartAction());
  const isNew = !messageSettings.id;
  const successMessage = `Message settings have been ${
    isNew ? 'created' : 'updated'
  }`;
  const result = await CallApiWithNotification<BatchResult<MessageSettings>>({
    executeFunction: isNew
      ? SettingsClient.createMessageSettings
      : SettingsClient.updateMessageSettings,
    params: messageSettings,
    successMessage,
    errorMessage: 'Creating message settings has failed.',
    dispatch,
  });

  if (result.status) {
    const {
      data: { updatedItems, createdItems },
    } = result;
    const updatedSettings = isNew ? createdItems : updatedItems;
    dispatch(
      loadSettingsSuccess({
        messageSettings: updatedSettings[0],
      }),
    );
  } else {
    dispatch(updateSettingsFailedAction(result.data));
  }
};

export const updateCrmSettings = (
  crmSettings: CrmSettings,
): AppThunkAction => async (dispatch, getState) => {
  let changedParam = '';
  let updateErrorMessage = '';
  const existingCrmSettings = getState().settings.crmSettings;
  // to setup snackbars messages, we need to check which crm settings param
  // has changed. To do that, we compare the setting param value with it existing
  // value in the redux store, if they're equal then that param doesn't changed
  // otherwise it does.
  if (
    existingCrmSettings?.defaultAssigneeIDs !== crmSettings.defaultAssigneeIDs
  ) {
    changedParam = 'Default assignees';
    updateErrorMessage = ERROR_CONSTS.UpdatingDefaultAssigneesFailed;
  }

  if (
    existingCrmSettings?.defaultLeadUserID !== crmSettings.defaultLeadUserID
  ) {
    changedParam = 'Default lead';
    updateErrorMessage = ERROR_CONSTS.UpdatingDefaultLeadFailed;
  }

  function success(successResponse: PortalSettings) {
    return {
      type: SettingTypes.UPDATE_CRM_SETTINGS_SUCCESS,
      payload: successResponse.fields.crmSettings,
    };
  }

  function failure(error: string) {
    return { type: SettingTypes.UPDATE_CRM_SETTINGS_FAILURE, error };
  }

  dispatch(updateSettingsStartAction());

  const result = await CallApiWithNotification({
    executeFunction: SettingsClient.updateCrmSettings,
    params: crmSettings,
    successMessage: `${changedParam} changed`,
    errorMessage: `${updateErrorMessage}`,
    dispatch,
  });

  if (result.status) {
    dispatch(success(result.data));
  } else {
    dispatch(failure(result.data));
  }
};

export const updateCrmTableSettings = (
  tableSettings: CrmTableSettings,
): AppThunkAction => async dispatch => {
  function success(payload: any) {
    return {
      type: SettingTypes.UPDATE_CRM_TABLE_SETTINGS_SUCCESS,
      payload,
    };
  }

  function failure(error: string) {
    return { type: SettingTypes.UPDATE_CRM_TABLE_SETTINGS_FAILURE, error };
  }

  dispatch(updateSettingsStartAction());
  const result = await CallApiWithNotification({
    executeFunction: SettingsClient.updateCrmTableSettings,
    params: tableSettings,
    successMessage: '',
    errorMessage: 'There was an error updating the table settings',
    dispatch,
  });

  if (result.status) {
    dispatch(success(tableSettings));
  } else {
    dispatch(failure(result.data));
  }
};

export const updateBillingSettings = (
  billingSettings: BillingSettings,
): AppThunkAction => async dispatch => {
  function success(payload: any) {
    return {
      type: SettingTypes.UPDATE_BILLING_SETTINGS_SUCCESS,
      payload,
    };
  }

  function failure(error: string) {
    return { type: SettingTypes.UPDATE_BILLING_SETTINGS_FAILURE, error };
  }

  dispatch(updateSettingsStartAction());

  // TODO: implement a generic util function to check
  // which param has been changed.
  const result = await CallApiWithNotification({
    executeFunction: SettingsClient.updateBillingSettings,
    params: billingSettings,
    successMessage: `Billing settings have been changed`,
    errorMessage: 'There was a problem updating the billing settings',
    dispatch,
  });

  if (result.status) {
    dispatch(success(result.data.fields.billingSettings));
  } else {
    dispatch(failure(result.data));
  }
};

export const updateModuleSettings = (
  moduleSettings: ModuleSettingsItem[],
): AppThunkAction => async dispatch => {
  // skip icon and label for extensions since those are saved in extensionSettings
  const updatedModules = moduleSettings.map(item => ({
    ...item,
    icon: item.type === 'local' || item.type === 'global' ? '' : item.icon,
    label: item.type === 'local' || item.type === 'global' ? '' : item.label,
  }));

  function success(payload: any) {
    return {
      type: SettingTypes.UPDATE_MODULE_SETTINGS_SUCCESS,
      payload,
    };
  }

  function failure(error: string) {
    return { type: SettingTypes.UPDATE_MODULE_SETTINGS_FAILURE, error };
  }

  dispatch(updateSettingsStartAction());

  // optimistic update modules
  dispatch(success(updatedModules));

  const result = await CallApiWithNotification({
    executeFunction: SettingsClient.updateModuleSettings,
    params: updatedModules,
    successMessage: `App settings have been changed`,
    errorMessage: 'There was a problem updating the app settings',
    dispatch,
  });

  if (!result.status) {
    dispatch(failure(result.data));
  }
};

export const updateFileSettings = (
  fileSettings: FileSettings,
): AppThunkAction => async dispatch => {
  function success(payload: any) {
    return {
      type: SettingTypes.UPDATE_FILE_SETTINGS_SUCCESS,
      payload,
    };
  }

  function failure(error: string) {
    return { type: SettingTypes.UPDATE_FILE_SETTINGS_FAILURE, error };
  }

  dispatch(updateSettingsStartAction());

  const result = await CallApiWithNotification({
    executeFunction: SettingsClient.updateFileSettings,
    params: fileSettings,
    successMessage: `File settings have been changed`,
    errorMessage: 'There was a problem updating the file settings',
    dispatch,
  });

  if (result.status) {
    dispatch(success(result.data.fields.fileSettings));
  } else {
    dispatch(failure(result.data));
  }
};

export const updateSettingsApiResultAction = (
  result: ApiWithNotificationResponseType<PortalSettings>,
): AppThunkAction => async dispatch => {
  // if update settings action is successful then updated portal settings entity
  // is returned and should dispatch updating portal settings action
  if (result.status) {
    dispatch(loadPortalSettingsAction([result.data]));
    dispatch(updateSettingsSucceededAction());
  } else {
    dispatch(updateSettingsFailedAction(result.errorMessage));
  }
};

/**
 * Initiate saving extension settings to portal settings and dispatch
 * actions to save response
 * @param extensionSettings new extension settings to save
 * @returns
 */
export const updateExtensionSettingsAction = (
  extensionSettings: ExtensionsSettings,
): AppThunkAction => async dispatch => {
  dispatch(updateSettingsStartAction());

  const result = await CallApiWithNotification<PortalSettings>({
    executeFunction: SettingsClient.updateExtensionSettings,
    params: extensionSettings,
    successMessage: '',
    errorMessage: 'There was a problem updating the app settings',
    dispatch,
  });

  dispatch(updateSettingsApiResultAction(result));
};

/**
 * Call api to disconnect app in portal settings. The resulting
 * portal settings are updated in redux store to show updated state
 * @param appName which app settings to disconnect
 */
export const disconnectAppAction = (
  appName: string,
): AppThunkAction => async dispatch => {
  dispatch(updateSettingsStartAction());

  const result = await CallApiWithNotification<PortalSettings>({
    executeFunction: SettingsClient.disconnectApp,
    params: appName,
    successMessage: `App has been disconnected`,
    errorMessage: 'There was a problem disconnecting the app',
    dispatch,
  });

  // if disconnect app is successful then updated portal settings entity
  // is returned and should dispatch updating portal settings action
  dispatch(updateSettingsApiResultAction(result));
};

export const createWebhookConfig = (
  webhookConfigData: WebhookForm,
): AppThunkAction => async dispatch => {
  function success(payload: WebhookConfigSet) {
    return {
      type: SettingTypes.ADD_WEBHOOK_CONFIG_SET_SUCCESS,
      payload,
    };
  }

  function failure(error: any) {
    return { type: SettingTypes.ADD_WEBHOOK_CONFIG_SET_FAILURE, error };
  }

  const result = await CallApiWithNotification<WebhookConfigSet>({
    executeFunction: SettingsClient.addWebhookConfigSet,
    params: webhookConfigData,
    successMessage: `Webhook configuration has been added`,
    errorMessage: 'There was a problem adding the webhook configuration',
    dispatch,
  });

  if (result.status) {
    dispatch(success(result.data));
  } else {
    dispatch(failure(result.data));
  }

  return result;
};

export const listWebhookConfigs = (): // webhookConfigData: WebhookForm,
AppThunkAction => async dispatch => {
  function success(payload: WebhookConfigSet[]) {
    return {
      type: SettingTypes.GET_WEBHOOK_CONFIG_SETS_SUCCESS,
      payload,
    };
  }
  const result = await SettingsClient.getWebhookConfigSets();
  if (result) {
    dispatch(success(result));
  }
};

export const deleteWebhookConfigSet = (
  id: string,
): AppThunkAction => async dispatch => {
  function success(deletedID: string) {
    return {
      type: SettingTypes.DELETE_WEBHOOK_CONFIG_SET_SUCCESS,
      payload: deletedID,
    };
  }
  try {
    await SettingsClient.deleteWebhookConfigSet(id);
    // result will be null after a successful delete
    dispatch(success(id));
  } catch (ex) {
    dispatch(
      alertSnackbar({
        errorMessage: `Webhook configuration not deleted.`,
      }),
    );
  }
};

export const updateWebhookConfigSet = (
  input: UpdateWebhookConfigSetInput,
): AppThunkAction => async dispatch => {
  function success(updatedConfigSet: WebhookConfigSet) {
    return {
      type: SettingTypes.UPDATE_WEBHOOK_CONFIG_SET_SUCCESS,
      payload: updatedConfigSet,
    };
  }

  const result = await CallApiWithNotification<WebhookConfigSet>({
    executeFunction: SettingsClient.updateWebhookConfigSet,
    params: input,
    successMessage: `Webhook configuration has been updated`,
    errorMessage: 'There was a problem updating the webhook configuration',
    dispatch,
  });

  if (result.status) {
    dispatch(success(result.data));
  }
  return result;
};
